/**
 * The arithmetic of a beat grid, kept apart from React so it can be tested.
 *
 * A grid is a tempo and one downbeat (a "1"); every other beat is a whole number
 * of beats from it, both ways. Bars are counted from the first downbeat at or
 * after the start of the file, which is bar 1; beats before it are bar 0, the
 * pickup.
 */

export interface Grid {
  bpm: number;
  downbeat: number;
  beatsPerBar: number;
}

export interface Beat {
  t: number;
  /** 1-based bar number; 0 is the pickup before the first downbeat. */
  bar: number;
  /** 0 is the "1". */
  beat: number;
}

export const beatSeconds = (g: Grid) => 60 / g.bpm;

/** The first downbeat at or after 0 seconds. */
export function firstDownbeat(g: Grid): number {
  const bar = beatSeconds(g) * g.beatsPerBar;
  return g.downbeat - Math.floor(g.downbeat / bar + 1e-9) * bar;
}

/** Every beat from `from` to `to` seconds (inclusive), never before 0. */
export function beatsBetween(g: Grid, from: number, to: number, limit = 20000): Beat[] {
  const b = beatSeconds(g);
  if (!(b > 0) || !(to >= from)) return [];
  const zero = firstDownbeat(g);
  const out: Beat[] = [];
  let k = Math.ceil((Math.max(0, from) - zero) / b - 1e-9);
  for (let t = zero + k * b; t <= to + 1e-9 && out.length < limit; k++, t = zero + k * b) {
    if (t < 0) continue;
    const beat = ((k % g.beatsPerBar) + g.beatsPerBar) % g.beatsPerBar;
    out.push({ t, bar: Math.floor(k / g.beatsPerBar) + 1, beat });
  }
  return out;
}

/** The beat nearest to `t`. */
export function nearestBeat(g: Grid, t: number): number {
  const b = beatSeconds(g);
  const zero = firstDownbeat(g);
  return zero + Math.round((t - zero) / b) * b;
}

/**
 * A tempo from taps (milliseconds), or null with fewer than four in a row.
 * A pause of over two seconds starts a new run, so a slip does not count.
 */
export function tempoFromTaps(taps: number[]): number | null {
  let run: number[] = [];
  for (const t of taps) {
    if (run.length && t - run[run.length - 1] > 2000) run = [];
    run.push(t);
  }
  if (run.length < 4) return null;
  const recent = run.slice(-16);
  const span = recent[recent.length - 1] - recent[0];
  if (span <= 0) return null;
  const bpm = (60000 * (recent.length - 1)) / span;
  return bpm >= 30 && bpm <= 300 ? Math.round(bpm * 100) / 100 : null;
}

/** Every `step`th bar is labelled, so numbers never crowd: at least `minPx` apart. */
export function barLabelStep(pxPerBar: number, minPx = 28): number {
  for (const step of [1, 2, 4, 8, 16, 32, 64]) if (pxPerBar * step >= minPx) return step;
  return 128;
}
